import { useState, useEffect, useContext } from 'react';
import { createPortal } from 'react-dom';
import { UserContext } from '../../../helper/ProtectedRoute';
import { useCodAutomationStore } from '../../../store/CodAutomation/codAutomationStore';
import { getTimezoneOffset } from '../../../services/axiosbase';
import GearIcon from '../../../assets/img/gearIcon.svg';
import EyeIcon from '../../../assets/img/eye_show.svg';
import { showErrorToast, showSuccessToast } from '../../../components/Toast/Toaster';
import CusModal from '../../../components/modal/CusModal';
import AdjustmentEntryModal from '../Cod/AdjustmentEntryModal';
import SettleIcon from '../../../assets/img/SettleIcon.svg';
import { ChevronDown, ChevronUp } from 'lucide-react';
import dayjs from 'dayjs';

const TableComponent = ({
  tableData,
  sortField,
  sorting,
  restrict,
  newPage,
  size,
  selectedRows,
  setSelectedRows,
  from,
  to,
  destinationCountry,
  includeDeductible,
  onViewClick,
  fetchPageData,
}) => {
  const [showAddEntryModal, setShowAddEntryModal] = useState(false);
  const [selectedSummaryId, setSelectedSummaryId] = useState('');

  const generateCodPayable = useCodAutomationStore((state) => state?.generateCodPayable);

  const [state] = useContext(UserContext);
  const [role, setRole] = useState(sessionStorage.getItem('user_access_role'));

  useEffect(() => {
    setRole(state.roles);
  }, [state.roles]);

  const handleSelectRow = (customer) => {
    if (selectedRows.includes(customer)) {
      setSelectedRows(selectedRows.filter((rowId) => rowId !== customer));
    } else {
      setSelectedRows([...selectedRows, customer]);
    }
  };

  const isRowSelected = (customer) => selectedRows.includes(customer);

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allNonDisabledRowIds = tableData.filter((row) => !row.disable).map((row) => row.customer);
      setSelectedRows(allNonDisabledRowIds);
    } else {
      setSelectedRows([]);
    }
  };

  const SortIcon = ({ field }) => {
    const currentField = sortField?.startsWith('-') ? sortField.slice(1) : sortField;
    if (currentField !== field) return null;
    return sortField?.startsWith('-')
      ? <ChevronDown className="size-3 inline ml-1" />
      : <ChevronUp className="size-3 inline ml-1" />;
  };

  const colCount =
    2 +
    (!restrict?.includes('Customer Account') ? 1 : 0) +
    (!restrict?.includes('Customer Name') ? 1 : 0) +
    (!restrict?.includes('COD Collected') ? 1 : 0) +
    (!restrict?.includes('Status') ? 1 : 0) +
    (!restrict?.includes('Destination Country') ? 1 : 0) +
    (!restrict?.includes('Bank Charges') ? 1 : 0) +
    (!restrict?.includes('Payable Amount') ? 1 : 0) +
    (!restrict?.includes('Email Sent') ? 1 : 0) +
    (!restrict?.includes('From Date') ? 1 : 0) +
    (!restrict?.includes('To Date') ? 1 : 0) +
    (!restrict?.includes('Disable') ? 1 : 0) +
    (!restrict?.includes('Deductible') ? 1 : 0) +
    (!restrict?.includes('Action') ? 1 : 0);

  return (
    <>
      {showAddEntryModal && createPortal(
        <CusModal
          show={showAddEntryModal}
          onHide={() => setShowAddEntryModal(false)}
          className="addVehicleCateg"
        >
          <AdjustmentEntryModal
            showModal={showAddEntryModal}
            setShowModal={setShowAddEntryModal}
            summaryId={selectedSummaryId}
            fetchPageData={fetchPageData}
          />
        </CusModal>,
        document.body
      )}
      <thead className="bg-gray-50 border-b border-gray-200">
        <tr>
          <th className="tbl-th sticky top-0 left-0 z-30 bg-gray-50 w-10">
            <div className="smallCheckbox">
              <input
                type="checkbox"
                id="tableCheckall"
                checked={tableData?.length > 0 && selectedRows?.length > 0 && selectedRows?.length === tableData?.filter((row) => !row.disable)?.length}
                onChange={handleSelectAll}
              />
              <label htmlFor="tableCheckall" />
            </div>
          </th>
          <th className="tbl-th sticky top-0 z-20 bg-gray-50 cursor-pointer hover:bg-gray-100 select-none" onClick={() => sorting('createdAt')}>
            # <SortIcon field="createdAt" />
          </th>
          {!restrict?.includes('Customer Account') && (
            <th className="tbl-th sticky top-0 z-20 bg-gray-50 cursor-pointer hover:bg-gray-100 select-none" onClick={() => sorting('customer')}>
              Customer Account <SortIcon field="customer" />
            </th>
          )}
          {!restrict?.includes('Customer Name') && (
            <th className="tbl-th sticky top-0 z-20 bg-gray-50 cursor-pointer hover:bg-gray-100 select-none" onClick={() => sorting('customerName')}>
              Customer Name <SortIcon field="customerName" />
            </th>
          )}
          {!restrict?.includes('COD Collected') && (
            <th className="tbl-th sticky top-0 z-20 bg-gray-50 cursor-pointer hover:bg-gray-100 select-none" onClick={() => sorting('codCollected')}>
              COD Collected <SortIcon field="codCollected" />
            </th>
          )}
          {!restrict?.includes('Status') && (
            <th className="tbl-th sticky top-0 z-20 bg-gray-50 cursor-pointer hover:bg-gray-100 select-none" onClick={() => sorting('status')}>
              Status <SortIcon field="status" />
            </th>
          )}
          {!restrict?.includes('Destination Country') && (
            <th className="tbl-th sticky top-0 z-20 bg-gray-50 cursor-pointer hover:bg-gray-100 select-none" onClick={() => sorting('destinationCountry')}>
              Destination Country <SortIcon field="destinationCountry" />
            </th>
          )}
          {!restrict?.includes('Bank Charges') && (
            <th className="tbl-th sticky top-0 z-20 bg-gray-50 select-none">
              Bank Charges
            </th>
          )}
          {!restrict?.includes('Payable Amount') && (
            <th className="tbl-th sticky top-0 z-20 bg-gray-50 select-none">
              Payable Amount
            </th>
          )}
          {!restrict?.includes('Email Sent') && (
            <th className="tbl-th sticky top-0 z-20 bg-gray-50 cursor-pointer hover:bg-gray-100 select-none" onClick={() => sorting('emailSent')}>
              Email Sent <SortIcon field="emailSent" />
            </th>
          )}
          {!restrict?.includes('From Date') && (
            <th className="tbl-th sticky top-0 z-20 bg-gray-50 select-none">From Date</th>
          )}
          {!restrict?.includes('To Date') && (
            <th className="tbl-th sticky top-0 z-20 bg-gray-50 select-none">To Date</th>
          )}
          {!restrict?.includes('Disable') && (
            <th className="tbl-th sticky top-0 z-20 bg-gray-50 select-none">Disable</th>
          )}
          {!restrict?.includes('Deductible') && (
            <th className="tbl-th sticky top-0 z-20 bg-gray-50 select-none">Deductible</th>
          )}
          {!restrict?.includes('Action') && (
            <th className="tbl-th sticky top-0 right-0 z-20 bg-gray-50 text-left">Action</th>
          )}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200">
        {!tableData?.length ? (
          <tr>
            <td colSpan={colCount} className="px-6 py-12 text-center text-gray-500">No records found</td>
          </tr>
        ) : (
          tableData.map((item, i) => (
            <tr
              key={item?.customer}
              className={`hover:bg-gray-50 transition-colors cursor-pointer ${
                selectedRows?.includes(item?.customer)
                  ? 'bg-red-50'
                  : i % 2 === 0
                  ? 'bg-white'
                  : 'bg-gray-50/50'
              }`}
            >
              <td className="tbl-td sticky left-0 z-10 bg-white">
                <div className={`smallCheckbox ${item?.disable ? 'disabledCheckbox' : ''}`}>
                  <input
                    type="checkbox"
                    id={`tableCheck${i}`}
                    checked={isRowSelected(item?.customer)}
                    onChange={() => handleSelectRow(item?.customer)}
                    disabled={item?.disable}
                  />
                  <label htmlFor={`tableCheck${i}`} />
                </div>
              </td>
              <td className="tbl-td">{newPage * size + (i + 1)}</td>
              {!restrict?.includes('Customer Account') && <td className="tbl-td">{item?.customer}</td>}
              {!restrict?.includes('Customer Name') && <td className="tbl-td">{item?.customerName}</td>}
              {!restrict?.includes('COD Collected') && <td className="tbl-td">{item?.codCollected ?? '-'}</td>}
              {!restrict?.includes('Status') && <td className="tbl-td">{item?.status ?? '-'}</td>}
              {!restrict?.includes('Destination Country') && <td className="tbl-td">{item?.destinationCountry ?? '-'}</td>}
              {!restrict?.includes('Bank Charges') && <td className="tbl-td">{item?.bankCharges ?? '-'}</td>}
              {!restrict?.includes('Payable Amount') && <td className="tbl-td">{item?.payable}</td>}
              {!restrict?.includes('Email Sent') && <td className="tbl-td">{item?.emailSent ? 'Yes' : 'No'}</td>}
              {!restrict?.includes('From Date') && (
                <td className="tbl-td">
                  {item?.from ? dayjs(item?.from).format('DD MMM YYYY, hh:mm:ss A') : null}
                </td>
              )}
              {!restrict?.includes('To Date') && (
                <td className="tbl-td">
                  {item?.to ? dayjs(item?.to).format('DD MMM YYYY, hh:mm:ss A') : null}
                </td>
              )}
              {!restrict?.includes('Disable') && <td className="tbl-td">{item?.disable ? 'Yes' : 'No'}</td>}
              {!restrict?.includes('Deductible') && <td className="tbl-td">{item?.deductible ? 'Yes' : 'No'}</td>}
              {!restrict?.includes('Action') && (
                <td className="tbl-td sticky right-0 z-10 bg-white">
                  <div className="flex items-center justify-start gap-1">
                    <button
                      className="p-1.5 bg-white hover:!bg-gray-100 rounded transition-colors"
                      title="View"
                      onClick={() => onViewClick({ customer: item?.customer, from: item?.from ?? from, to: item?.to ?? to, destinationCountry: item?.destinationCountry ?? destinationCountry, summaryItem: item })}
                    >
                      <img src={EyeIcon} className="h-4 w-4 turnBlack" alt="view" />
                    </button>
                    <button
                      className="p-1.5 bg-white hover:!bg-gray-100 rounded transition-colors"
                      title="Generate"
                      onClick={async () => {
                        try {
                          const response = await generateCodPayable({
                            timezone: getTimezoneOffset(),
                            generateRequests: [
                              {
                                customerAccount: item?.customer,
                                summaryId: item?.summaryId,
                                from: item?.from ?? from,
                                to: item?.to ?? to,
                                destinationCountry: item?.destinationCountry ?? destinationCountry,
                                includeDeductible: item?.deductible ?? includeDeductible,
                              },
                            ],
                          });
                          if (response?.data?.status === 'SUCCESS') {
                            showSuccessToast(response?.data?.msg || 'Generated Successfully');
                            setSelectedRows([]);
                            fetchPageData();
                          } else {
                            showErrorToast(response?.response?.data?.msg || 'ERROR');
                          }
                        } catch (error) {
                          showErrorToast(error?.msg || 'ERROR');
                        }
                      }}
                      disabled={item?.disable}
                    >
                      <img src={GearIcon} className="h-4 w-4 turnBlack" alt="generate" />
                    </button>
                    {!item?.disable && (
                      <button
                        className="p-1.5 bg-white hover:!bg-gray-100 rounded transition-colors"
                        title="Add adjustment entry"
                        onClick={() => {
                          setShowAddEntryModal(true);
                          setSelectedSummaryId(item?.summaryId);
                        }}
                      >
                        <img src={SettleIcon} className="h-4 w-4 turnBlack" alt="adjust" />
                      </button>
                    )}
                  </div>
                </td>
              )}
            </tr>
          ))
        )}
      </tbody>
    </>
  );
};

export default TableComponent;
